import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Container, Form, ListGroup, ListGroupItem, Table } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';
import { useField, useFormikContext } from 'formik';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { format as formatDate } from 'date-fns';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { useDropzone } from 'react-dropzone';
import { IconGPSLocationCrosshairs } from '../awpIcons';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import FormColorInput from './AwpColorInput';
import { formatBytes } from './Helpers/string';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { ActionMenuView } from '@awp/components/Common/AwpActionMenus';
import { ToolbarPlugin } from './ToolbarPlugin';
import { MaxLengthPlugin } from './Helpers/SystemNotificationMaxLengthPlugin';
import { OnChangePlugin } from './Helpers/SystemNotificationOnChangePlugin';
import { TagsInput } from 'react-tag-input-component';

export const FieldError = ({ meta }) => {
  if (!meta.touched || !meta.error) return <></>;
  const errors = Array.isArray(meta.error) ? meta.error : [meta.error];

  if (errors.length === 1 && (!errors[0] || Object.keys(errors[0]).length === 0)) return <></>;

  return (
    <div className="text-danger mt-1">
      <ul>
        {errors.map((error, i) => (
          <li key={i}>{error}</li>
        ))}
      </ul>
    </div>
  );
};

export const CustomFieldError = ({ meta1, meta2, message }) => {
  if (!meta1.error && !meta2.error) return <></>;

  const error = (
    <div className="text-danger mt-1">
      <ul>
        <li>{message}</li>
      </ul>
    </div>
  );

  return error;
};

// TODO: Move to a service file, as this function is specific to the area code input
export const AreaCodeInput = ({ label, instruction, readOnly = false, onChange, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const inputRef = useRef(null);

  const handleChange = (e) => {
    if (readOnly) return;

    let value = e.target.value.replace(/[^0-9]/g, '');

    if (field.value.endsWith('00') && value.length < field.value.length) {
      value = value.slice(0, -2);
    }

    if (value.length > 3) {
      value = value.slice(0, 3);
    }

    if (value.length === 3) {
      value += '00';
    }

    helpers.setValue(value);
    if (inputRef.current) {
      inputRef.current.value = value;
    }

    if (onChange) onChange(e);
  };

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        {!readOnly && instruction && (
          <small style={{ alignSelf: 'flex-end', marginBottom: '-0.05rem' }}>{instruction}</small>
        )}
      </div>
      {readOnly ? (
        <div className="centered">{field.value ? <span>{field.value}</span> : <span>&nbsp;&nbsp;</span>}</div>
      ) : (
        <>
          <Form.Control
            ref={inputRef}
            className="text-input"
            {...props}
            autoComplete="off"
            defaultValue={field.value || ''}
            onChange={handleChange}
          />
          {meta.touched && meta.error && <div className="text-danger">{meta.error}</div>}
        </>
      )}
    </Form.Group>
  );
};

export const SubAreaCodeInput = ({ label, instruction, readOnly = false, parentId, onChange, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const prefix = parentId ? String(parentId).slice(0, 3) : '';

  useEffect(() => {
    if ((!field.value || !field.value.startsWith(prefix)) && !meta.touched) {
      helpers.setValue(prefix, false);
    }
  }, [prefix, field.value, meta.touched]);

  const handleChange = (e) => {
    let inputValue = e.target.value;

    if (!inputValue.startsWith(prefix)) {
      const userInput = inputValue.slice(prefix.length).replace(/\D/g, '');
      inputValue = prefix + userInput;
    } else {
      inputValue = inputValue.replace(/\D/g, '');
    }

    const maxLength = prefix.length + 2;
    if (inputValue.length > maxLength) {
      inputValue = inputValue.slice(0, maxLength);
    }

    helpers.setValue(inputValue);

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {readOnly ? (
        <div className="centered">
          {field.value ? <span {...props}>{field.value}</span> : <span>&nbsp;&nbsp;</span>}
        </div>
      ) : (
        <>
          <Form.Control
            className="text-input"
            {...props}
            autoComplete="off"
            value={field.value || ''}
            onChange={handleChange}
            maxLength={5}
          />
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};

export const FormTagsInput = (props) => {
  const { label, instruction, readOnly = false, ...rest } = props;
  const [field, meta, { setValue }] = useField(rest);

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        {!readOnly && instruction && (
          <small style={{ alignSelf: 'flex-end', marginBottom: '-0.05rem' }}>{instruction}</small>
        )}
      </div>
      <TagsInput
        {...field}
        {...rest}
        disabled={readOnly}
        onChange={(e) => {
          return setValue(e);
        }}
      />
      <FieldError meta={meta} />
    </Form.Group>
  );
};

export const FormInput = ({ label, instruction, readOnly = false, onChange, ...props }) => {
  const [field, meta] = useField(props);

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {readOnly ? (
        <div className="centered">
          {field.value ? <span {...props}>{field.value}</span> : <span>&nbsp;&nbsp;</span>}
        </div>
      ) : (
        <>
          <Form.Control
            className="text-input"
            {...field}
            {...props}
            autoComplete="off"
            value={field.value || ''} // Make sure it always has a value
            onChange={(e) => {
              field.onChange(e);
              if (onChange) onChange(e);
            }}
          />
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};

export const FormPhoneNumber = ({ label, instruction, readOnly = false, setFieldValue, ...props }) => {
  const [field, meta] = useField(props);
  const { name, value } = field;
  const formatPhoneNumber = (inputValue) => {
    if (!inputValue) return inputValue;

    const cleanedValue = inputValue.replace(/\D/g, '');
    if (cleanedValue) {
      if (cleanedValue.length <= 3) {
        return cleanedValue;
      } else if (cleanedValue.length <= 6) {
        return `${cleanedValue.slice(0, 3)}-${cleanedValue.slice(3)}`;
      } else {
        return `${cleanedValue.slice(0, 3)}-${cleanedValue.slice(3, 6)}-${cleanedValue.slice(6, 10)}`;
      }
    }
    return inputValue;
  };

  const handleChange = (e) => {
    setFieldValue(name, e.target.value.replace(/\D/g, ''));
  };

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {readOnly ? (
        <div className="centered">
          {field.value ? <span {...props}>{formatPhoneNumber(value)}</span> : <span>&nbsp;&nbsp;</span>}
        </div>
      ) : (
        <>
          <Form.Control
            className="text-input"
            {...field}
            {...props}
            autoComplete="off"
            value={formatPhoneNumber(value)}
            onChange={handleChange}
            maxLength="12"
          />
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};
export const FormForecastLevel = ({ label, instruction, readOnly = false, ...props }) => {
  // const [field, meta] = useField(props);
  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {/*
        <div className="centered">
          {field.value ? (
            field.value.split('\r\n').map((line, index) => <div key={index}>{line}</div>)
          ) : (
            <span>&nbsp;&nbsp;</span>
          )}
        </div>
        */}
    </Form.Group>
  );
};
// used for spatial field which contains sub Label
export const FormSpatialInput = ({ label, instruction, subLabel, readOnly = false, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {readOnly ? (
        <div>
          <div className="centered">
            {field.value ? <span {...props}>{field.value}</span> : <span>&nbsp;&nbsp;</span>}
          </div>
          <div>{subLabel}</div>
        </div>
      ) : (
        <>
          <Form.Control className="text-input" {...field} {...props} autoComplete="off" />
          <div>{subLabel}</div>
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};
export const FormTextReadOnly = ({ label, textValue }) => {
  return (
    <Form.Group className="my-1">
      <Form.Label>
        <b>{label}</b>
      </Form.Label>
      <div className="centered">{textValue ? <span>{textValue}</span> : <span>&nbsp;&nbsp;</span>}</div>
    </Form.Group>
  );
};
export const FormHorizontalTextReadOnly = ({ label, textValue, isBold = true }) => {
  return (
    <Form.Group className="my-1">
      {isBold ? (
        <Form.Label>
          <b>{label}</b>{' '}
        </Form.Label>
      ) : (
        <Form.Label> {label}</Form.Label>
      )}
      <div className="centered" style={{ display: 'inline-block' }}>
        {textValue ? <span style={{ marginLeft: '0.4rem' }}>{textValue}</span> : <span>&nbsp;&nbsp;</span>}
      </div>
    </Form.Group>
  );
};
export const FormTextArea = ({
  label,
  instruction,
  readOnly = false,
  maxLength = 250,
  showCharacterCount = false,
  ...props
}) => {
  const [field, meta] = useField(props);
  const [charCount, setCharCount] = useState(field.value?.length || 0);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCharCount(value.length);
    field.onChange(e); // Update Formik field value
  };

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {readOnly ? (
        <>
          <Form.Control as="textarea" {...field} {...props} disabled style={{ backgroundColor: 'white' }} />
        </>
      ) : (
        <>
          <Form.Control
            as="textarea"
            {...field}
            {...props}
            onChange={handleInputChange}
            disabled={readOnly}
            autoComplete="off"
          />
          {showCharacterCount && maxLength >= charCount && (
            <div className="d-flex justify-content-between mt-1">
              <small className="text-muted">
                {charCount} / {maxLength} characters
              </small>
            </div>
          )}
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};

export const FormEmailReadOnly = ({ label, textValue }) => {
  const href = `mailto:${textValue}`;
  return (
    <Form.Group className="my-1">
      <Form.Label>
        <b>{label}</b>
      </Form.Label>
      <div className="centered">
        {textValue ? (
          <a href={href}>
            <span>{textValue}</span>
          </a>
        ) : (
          <span>&nbsp;&nbsp;</span>
        )}
      </div>
    </Form.Group>
  );
};

export const FormSwitch = ({ label, readOnly = false, ...props }) => {
  const [field, meta] = useField(props);

  const isChecked = readOnly ? !!field.value : typeof field.checked !== 'undefined' ? field.checked : !!field.value;

  return (
    <Form.Group className="my-1">
      <Form.Label htmlFor={props.id || props.name}>
        <b>{label}</b>
      </Form.Label>
      {readOnly ? (
        <Form.Check type="switch" id={props.id || props.name} checked={isChecked} {...props} disabled />
      ) : (
        <>
          <Form.Check type="switch" id={props.id || props.name} checked={isChecked} {...field} {...props} />
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};

export const FormRadioGroup = ({ label, readOnly = false, useMargin = false, textValue, children, ...props }) => {
  const [field, meta] = useField(props);
  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;
  return (
    <Form.Group className={className}>
      {label && (
        <Form.Label htmlFor={props.id || props.name}>
          <b>{label}</b>
        </Form.Label>
      )}

      {readOnly ? (
        <div className="centered">{textValue ? <span>{textValue}</span> : <span>&nbsp;&nbsp;</span>}</div>
      ) : (
        <>
          {children}
          <FieldError meta={meta} />
        </>
      )}
    </Form.Group>
  );
};

export const FormRadio = ({ readOnly = false, ...props }) => {
  const [field, meta] = useField(props);
  const checked = field.value === props.value;

  return readOnly ? (
    <Form.Check type="radio" {...field} {...props} checked={checked} disabled />
  ) : (
    <Form.Check type="radio" {...field} {...props} checked={checked} />
  );
};

export const FormCheckbox = ({ readOnly = false, useMargin = false, onCheck, ...props }) => {
  const [field, meta, { setTouched }] = useField(props);
  const onClick = (e) => {
    setTouched(true);
    if (onCheck != null) {
      onCheck(e, field);
    }
  };
  const isChecked = readOnly
    ? field.value
    : isValueEmpty(field.checked) && !isValueEmpty(field.value)
    ? field.value
    : field.checked; // Determine the checked state
  const className = `${useMargin ? 'mb-20' : ''}`;
  return (
    <>
      <Form.Check
        className={className}
        type="checkbox"
        {...field}
        {...props}
        onBlur={setTouched}
        onClick={onClick}
        checked={isChecked}
        disabled={readOnly}
      />
      <FieldError meta={meta} />
    </>
  );
};

export const FormGroupCheckbox = ({ readOnly = false, exclude, ...props }) => {
  const [field, meta] = useField(props);

  const isChecked = () => {
    const id = props.id;
    const list = props.value; //value should be null or array
    if (!list || list.length === 0) return false;
    if (list.includes(id + '')) return true;
  };
  return readOnly ? (
    <Form.Check type="checkbox" {...field} {...props} checked={isChecked()} disabled />
  ) : (
    <Form.Check type="checkbox" {...field} {...props} checked={isChecked()} />
  );
};

export const FormSelect = ({
  label,
  textValue,
  readOnly = false,
  instruction,
  focus = false,
  useMargin = false,
  useSearchByValue = false,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(props);
  const onChange = (value) => {
    setTimeout(() => setValue(value)); // required delay for yup validation
    if (props.postChange && props.postChange instanceof Function) props.postChange(value);
  };
  const valueFilterOption = useSearchByValue
    ? (option, inputValue) => {
        return option.value.toLowerCase().startsWith(inputValue.toLowerCase());
      }
    : null;
  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;
  return (
    <>
      <Form.Group className={className}>
        <div className="d-flex">
          <Form.Label className="flex-grow-1" style={props.labelStyle} htmlFor={props.id || props.name}>
            <b>{label}</b>
          </Form.Label>
          <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
            <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
          </div>
        </div>
        {readOnly ? (
          <>
            {props && props.isMulti && field && field.value && field.value.length > 0 ? (
              <>
                {field.value.map((v, i) => {
                  return (
                    <div key={'form_selected_opt_' + i} className="centered">
                      {v ? <span>{v.label}</span> : <span>&nbsp;&nbsp;</span>}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="centered">
                {field.value || textValue ? <span>{(field.value || textValue).label}</span> : <span>&nbsp;&nbsp;</span>}
              </div>
            )}
          </>
        ) : useSearchByValue ? (
          <>
            <Select
              className={props.className}
              {...props}
              value={field.value || textValue}
              onChange={onChange}
              filterOption={valueFilterOption}
              onBlur={setTouched}
              isClearable={props.isClearable}
              autoFocus={focus}
            />
            <FieldError meta={meta} />
          </>
        ) : (
          <>
            <Select
              className={props.className}
              {...props}
              value={field.value || textValue}
              onChange={onChange}
              onBlur={setTouched}
              isClearable={props.isClearable}
              autoFocus={focus}
            />
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};
export const FormTreeSelect = ({
  label,
  options = [],
  selectMode = 'radioSelect',
  placeholder = 'Select...',
  onChange,
  readOnly = false,
  disabled = false,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(props);

  //currentNode example
  // 	{
  //     "label": "52600 - AMSBURY",
  //     "value": 92075,
  //     "disabled": false,
  //     "parentId": 93732,
  //     "_depth": 1,
  //     "_id": "area-1-2",
  //     "_parent": "area-1",
  //     "hide": false,
  //     "checked": true,
  //     "_focused": true
  // }
  return (
    <>
      <Form.Group className="my-1">
        <Form.Label style={props.labelStyle} htmlFor={props.id || props.name}>
          <b>{label}</b>
        </Form.Label>
        {readOnly ? (
          <>
            {props && field && field.value && field.value.length > 0 ? (
              <>
                {field.value.map((v, i) => {
                  return (
                    <div key={'form_selected_opt_' + i} className="centered">
                      {v ? <span>{v.label}</span> : <span>&nbsp;&nbsp;</span>}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="centered">
                {field.value ? <span>{field.value.label}</span> : <span>&nbsp;&nbsp;</span>}
              </div>
            )}
          </>
        ) : (
          <>
            <DropdownTreeSelect
              className={'awp-tree-select ' + props.className}
              data={options}
              id={props.id}
              onChange={onChange}
              onBlur={setTouched}
              texts={{ placeholder: placeholder }}
              clearSearchOnChange
              keepTreeOnSearch
              noChildrenLabel="No option"
              mode={selectMode}
              keepOpenOnSelect={false}
              showPartiallySelected={false}
              inlineSearchInput={true}
              disabled={disabled}
            />
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};

export const FormListWithAction = ({
  label,
  colLabel,
  id,
  name,
  items,
  readOnly,
  onActionClick,
  columns = [],
  hasActionLink = true,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(name);
  const maxHeightThreshold = 10;
  const tableStyle =
    items.length > maxHeightThreshold ? { maxHeight: '300px', overflowY: 'auto', display: 'block' } : {};

  return (
    <Form.Group className="my-1">
      <Form.Label style={props.labelStyle} htmlFor={props.id || props.name}>
        <b>{label}</b>
      </Form.Label>
      <div className="flex-grow-1">
        <b>{colLabel}</b>
      </div>

      {items.length > 0 ? (
        <div style={tableStyle}>
          <Table id={id} striped bordered hover>
            <thead>
              <tr>
                {columns.map((column, idx) => (
                  <th style={{ padding: '10px 15px' }} key={idx}>
                    {column.label}
                  </th>
                ))}
                {hasActionLink && <th style={{ padding: '10px 15px' }}>Link</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.value} style={{ backgroundColor: '#fff' }}>
                  {columns.map((column, colIdx) => (
                    <td key={colIdx}>{item[column.key]}</td>
                  ))}
                  {hasActionLink && (
                    <td style={{ textAlign: 'center', width: '20px' }}>
                      <ActionMenuView onClick={() => onActionClick(item)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="mt-4">
          <p>No records found</p>
        </div>
      )}

      {!readOnly && <FieldError meta={meta} />}
    </Form.Group>
  );
};

export const FormList = ({ label, colLabel, id, name, permissions, readOnly }) => {
  const [field, meta, { setValue, setTouched }] = useField(name);

  const existingPermissions = permissions.filter((permission) => meta.initialValue.includes(permission.value));

  const onChange = (e, permission) => {
    const values = [...field.value];
    if (e.target.checked) {
      setValue([...field.value, permission.value]);
    } else {
      const idx = values.indexOf(permission.value);
      values.splice(idx, 1);
      setValue(values);
    }
  };

  return (
    <Form.Group className="my-1">
      <Container>
        <h3>{label}</h3>
        {readOnly ? (
          <ListGroup>
            {existingPermissions.map((permission, idx) => (
              <ListGroupItem key={idx}>{permission.label}</ListGroupItem>
            ))}
          </ListGroup>
        ) : (
          <>
            <FieldError meta={meta} />
            <Table id={id} name={name} striped bordered hover>
              <thead>
                <tr>
                  <th>{colLabel}</th>
                  <th>Select</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((permission) => (
                  <tr key={permission.value}>
                    <td>{permission.label}</td>
                    <td>
                      <Form.Check
                        style={{ textAlign: 'center' }}
                        aria-label="permission"
                        value={permission.value}
                        checked={field.value.includes(permission.value)}
                        onChange={(e) => onChange(e, permission)}
                        onBlur={setTouched}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Container>
    </Form.Group>
  );
};

export const FormDatePicker = ({
  label,
  textValue,
  readOnly = false,
  timeFormat = 'HH:mm',
  timeIntervals = 60,
  showTimeSelect = false,
  showTime = { isEnabled: true },
  dateFormat = ['yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'yyyyMMdd'],
  onClickOutside = () => {},
  onButtonClick = () => {},
  onValueChange = (value) => {},
  hasButton = false,
  instruction,
  focus = false,
  useMargin = false,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(props);
  const onChange = (value) => {
    setTimeout(() => {
      setTouched(true);
      setValue(value);
      onValueChange(value);
    }); // required delay for yup validation
  };
  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;
  return (
    <>
      <Form.Group className={className}>
        <div className="d-flex">
          <Form.Label className="flex-grow-1 mr-2" style={props.labelStyle} htmlFor={props.id || props.name}>
            <b>{label}</b>
          </Form.Label>
          <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
            <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
          </div>
        </div>

        {readOnly ? (
          <div className="centered">
            {field.value ? <span>{formatDate(new Date(field.value), dateFormat[0])}</span> : <span>&nbsp;&nbsp;</span>}
          </div>
        ) : hasButton ? (
          <>
            <div className="d-flex">
              <DatePicker
                selected={field.value}
                className="form-control input-sm"
                dateFormat={dateFormat}
                showTimeSelect={showTimeSelect} //control time selection
                showTime={showTime}
                closeOnSelect={true}
                timeFormat={timeFormat}
                timeIntervals={timeIntervals}
                onChange={onChange}
                onClickOutside={onClickOutside}
                autoFocus={focus}
              />
              <Button variant="outline-primary flex mx-2" onClick={onButtonClick}>
                Current Date/Time
              </Button>
            </div>
            <FieldError meta={meta} />
          </>
        ) : (
          <>
            <DatePicker
              selected={field.value}
              className="form-control input-sm"
              dateFormat={dateFormat}
              showTimeSelect={showTimeSelect} //control time selection
              showTime={showTime}
              closeOnSelect={true}
              timeFormat={timeFormat}
              timeIntervals={timeIntervals}
              onChange={onChange}
              onClickOutside={onClickOutside}
              autoFocus={focus}
            />
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};
export const FormDatePickerWithChange = ({
  label,
  textValue,
  readOnly = false,
  timeFormat = 'HH:mm',
  timeIntervals = 60,
  showTimeSelect = false,
  showTime = { isEnabled: true },
  dateFormat = ['yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'yyyyMMdd'],
  onChange,
  disabled = false,
  instruction,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(props);
  return (
    <>
      <Form.Group className="my-1">
        <div className="d-flex">
          <Form.Label className="flex-grow-1 mr-2" style={props.labelStyle} htmlFor={props.id || props.name}>
            <b>{label}</b>
          </Form.Label>
          <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
            <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
          </div>
        </div>

        {readOnly ? (
          <div className="centered">
            {field.value ? <span>{formatDate(new Date(field.value), dateFormat[0])}</span> : <span>&nbsp;&nbsp;</span>}
          </div>
        ) : (
          <>
            <DatePicker
              selected={field.value}
              className="form-control input-sm"
              dateFormat={dateFormat}
              showTimeSelect={showTimeSelect} //control time selection
              showTime={showTime}
              closeOnSelect={true}
              timeFormat={timeFormat}
              timeIntervals={timeIntervals}
              onChange={onChange}
              disabled={disabled}
            />
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};
export const FormNumberInput = ({
  label,
  instruction,
  readOnly = false,
  disabled = false,
  isAllowed = () => true,
  postChange,
  postBlur,
  warningMessage,
  useMargin = false,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(props);
  const formikContext = useFormikContext();

  const onBlur = () => {
    setTouched();
    if (postBlur && postBlur instanceof Function) postBlur(field.value);
  };

  const onChange = (value) => {
    setTouched(true);
    setValue(value);
    if (postChange && postChange instanceof Function) postChange(value);
  };

  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;
  return (
    <>
      <Form.Group className={className}>
        <div className="d-flex">
          <Form.Label className="flex-grow-1">
            <b>{label}</b>
            {warningMessage ? <b style={{ color: '#ff9800' }}>{` - ${warningMessage}`}</b> : null}
          </Form.Label>
          <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
            <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
          </div>
        </div>
        {readOnly ? (
          <div className="centered">
            {field.value != undefined || props.defaultValue != undefined ? (
              <span {...props}>{field.value != undefined ? field.value : props.defaultValue}</span>
            ) : (
              <span>&nbsp;&nbsp;</span>
            )}
          </div>
        ) : (
          <>
            <Form.Control
              type="hidden"
              {...field}
              // eslint-disable-next-line no-undef
              {..._.omit(props, ['decimalScale', 'getInputRef', 'id'])}
              isInvalid={meta.error && meta.touched}
              value={field.value ?? props.defaultValue ?? ''}
            />
            <NumericFormat
              valueIsNumericString={true}
              disabled={disabled}
              onBlur={(e) => {
                onBlur(e);
              }}
              className="form-control input-sm"
              thousandSeparator={true}
              prefix={props.prefix}
              value={field.value ?? props.defaultValue ?? ''}
              decimalScale={props.decimalScale || undefined}
              onValueChange={(value) => {
                onChange(value.value);
              }}
              isAllowed={isAllowed}
            >
              {props.children}
            </NumericFormat>
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};

//heavily customized dropdown with button
export const DropdownWithGeolocate = ({
  label,
  postChange = false,
  readOnly = false,
  buttonAction = false,
  disableGPS = false,
  focus = false,
  warningMessage = false,
  useMargin = false,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(props);

  const onChange = (value) => {
    setTimeout(() => setValue(value)); // required delay for yup validation
    if (postChange && postChange instanceof Function) postChange(value);
  };
  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;
  return (
    <>
      <Form.Group className={className}>
        <Form.Label style={props.labelStyle} htmlFor={props.id || props.name}>
          {warningMessage ? <b style={{ color: '#ff9800' }}>{label}</b> : <b>{label}</b>}
        </Form.Label>
        {readOnly ? (
          <div className="centered">{field.value ? <span>{field.value.label}</span> : <span>&nbsp;&nbsp;</span>}</div>
        ) : (
          <>
            <div className="input-group">
              <Select
                className={disableGPS ? 'full-width' : 'partial-width'}
                {...props}
                value={field.value}
                onChange={onChange}
                onBlur={setTouched}
                autoFocus={focus}
              />
              {disableGPS ? null : (
                <div className="input-group-append">
                  <IconGPSLocationCrosshairs
                    alt="Geolocate"
                    style={{
                      width: '2rem',
                      height: '2rem',
                      paddingTop: '.5rem',
                      paddingLeft: '.5rem',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      if (buttonAction && buttonAction instanceof Function) buttonAction(e);
                    }}
                  />
                </div>
              )}
            </div>
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

// See https://react-dropzone.js.org/
export const FormDropZone = ({
  label,
  instruction,
  maxSize = 52428800, // 50MB
  multiple = false,
  accept,
  onChange = (acceptedFiles, rejectedFiles) => {},
  preview = false,
  ...props
}) => {
  const [field, meta, { setValue }] = useField(props);
  const [accepted, setAccepted] = useState([]);
  const [rejected, setRejected] = useState([]);

  function blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    const $acceptedFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const preview = URL.createObjectURL(file);
        const previewBase64 = await blobToBase64(file);

        return Object.assign(file, {
          preview,
          previewBase64,
        });
      }),
    );

    setAccepted($acceptedFiles);
    setRejected(rejectedFiles);
    setValue($acceptedFiles);

    if (onChange) onChange($acceptedFiles, rejectedFiles);
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, isDragActive } = useDropzone({
    accept,
    maxSize,
    multiple,
    onDrop,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  const acceptedFileItems = accepted.map((file) => (
    <li key={file.path}>
      {file.path} - {formatBytes(file.size)}
    </li>
  ));

  const fileRejectionItems = rejected.map(({ file, errors }) => (
    <li key={file.path}>
      {file.path} - {formatBytes(file.size)}
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  return (
    <>
      {
        // Preview
        preview &&
          accepted.length > 0 &&
          accepted.map((file, index) => (
            <img
              key={index}
              src={file.previewBase64}
              style={{ width: '100%' }}
              // Revoke data uri after image is loaded
              onLoad={() => {
                URL.revokeObjectURL(file.previewBase64);
              }}
            />
          ))
      }
      <Form.Group className="my-1">
        <div className="d-flex">
          <Form.Label className="flex-grow-1 mr-2" style={props.labelStyle} htmlFor={props.id || props.name}>
            <b>{label}</b>
          </Form.Label>
          {instruction && (
            <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
              <small style={{ alignSelf: 'flex-end' }}>{instruction}</small>
            </div>
          )}
        </div>

        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Drop the files here ...</p>
          ) : (
            <>
              <div className="text-center">
                <i className="fa fa-cloud-upload-alt fa-2x"></i>
                <div className="font-weight-bold">Browse a File</div>
                <div className="">Drag and drop a file here</div>
              </div>
            </>
          )}
        </div>

        <aside className="mt-2">
          {accepted.length > 0 && (
            <>
              <h4>Accepted files</h4>
              <ul>{acceptedFileItems}</ul>
            </>
          )}

          {rejected.length > 0 && (
            <>
              <h4>Rejected files</h4>
              <ul>{fileRejectionItems}</ul>
            </>
          )}
        </aside>
        <FieldError meta={meta} />
      </Form.Group>
    </>
  );
};

export const FormGpsPoint = ({ label, instruction, readOnly = false, useMargin = false, setFieldValue, ...props }) => {
  const [latitude, latitudeMeta] = useField('latitude');
  const [longitude, longitudeMeta] = useField('longitude');
  const [accuracy, accuracyMeta] = useField('accuracy');

  const [lat, setLat] = useState('');
  const [long, setLong] = useState('');

  const handleLatitude = (e) => {
    const value = e.target.value === '' ? null : e.target.value;
    setLat(value);
    setTimeout(() => {
      setFieldValue('latitude', value);
      setFieldValue('accuracy', null);
    });
  };

  const handleLongitude = (e) => {
    const value = e.target.value === '' ? null : e.target.value;
    setLong(value);
    setTimeout(() => {
      setFieldValue('longitude', value);
      setFieldValue('accuracy', null);
    });
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { coords } = position;
          setLat(coords.latitude);
          setLong(coords.longitude);
          setFieldValue('latitude', coords.latitude);
          setFieldValue('longitude', coords.longitude);
          setFieldValue('accuracy', coords.accuracy);
        },
        (error) => {
          console.error('Error getting location:', error.message);
        },
      );
    } else {
      console.error('Geolocation is not supported by your browser');
    }
  };

  useEffect(() => {
    if (latitude) {
      setLat(latitude.value);
    }

    if (longitude) {
      setLong(longitude.value);
    }
  }, []);

  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;

  return (
    <Form.Group className={className}>
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
        <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
          <small style={{ alignSelf: 'flex-end' }}>{!readOnly && instruction}</small>
        </div>
      </div>
      {readOnly ? (
        <div className="centered">
          <span>{`${
            latitude === null || latitude === undefined || latitude.value === null || latitude.value === undefined
              ? ''
              : latitude.value + ', '
          }${
            longitude === null || longitude === undefined || longitude.value === null || longitude.value === undefined
              ? ''
              : longitude.value
          }${
            accuracy === null || accuracy === undefined || accuracy.value === null || accuracy.value === undefined
              ? ''
              : ', ' + accuracy.value + 'meters'
          }`}</span>
        </div>
      ) : (
        <>
          <div className="d-flex">
            <Form.Control
              className="text-input mr-2"
              id="lat"
              name="lat"
              value={lat}
              onChange={handleLatitude}
              autoComplete="off"
              placeholder="Latitude"
            />
            <Form.Control
              className="text-input mr-2"
              id="long"
              name="long"
              value={long}
              onChange={handleLongitude}
              autoComplete="off"
              placeholder="Longitude"
            />
            <div className="input-group-append">
              <IconGPSLocationCrosshairs
                alt="Geolocate"
                style={{
                  width: '2rem',
                  height: '2rem',
                  paddingTop: '.5rem',
                  paddingLeft: '.5rem',
                  cursor: 'pointer',
                }}
                onClick={handleGetLocation}
              />
            </div>
          </div>
          {latitude.value && longitude.value && !latitudeMeta.error && accuracy.value && (
            <>
              <i className="fas fa-exclamation-circle mx-2 text-bcgold" />
              <span>{`Accuracy: ${accuracy.value} meters`}</span>
            </>
          )}
          <CustomFieldError meta1={latitudeMeta} meta2={longitudeMeta} message="Invalid British Columbia Coordinates" />
        </>
      )}
    </Form.Group>
  );
};

export const FormSelectNumberInput = ({
  label,
  textValue,
  postChange = false,
  readOnly = false,
  instruction,
  focus = false,
  isAllowed = () => true,
  postBlur,
  warningMessage,
  id,
  name,
  options,
  useMargin = false,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(id);
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (field) {
      const initValue = options.find((x) => x.value == field.value);
      // if (initValue && selectedValue?.value !== 'Custom') {
      if (initValue) {
        setSelectedValue(initValue);
      } else if (field.value) {
        setSelectedValue(options.find((x) => x.value == 'Custom'));
      } else {
        setSelectedValue(null);
      }
    } else {
      setSelectedValue(null);
    }
  }, [field.value]);

  const onBlur = () => {
    setTouched();
    if (postBlur && postBlur instanceof Function) postBlur(field.value);
  };

  const onChange = (value) => {
    setTouched(true);
    setValue(value);
    if (postChange && postChange instanceof Function) postChange(value);
  };

  const onSelectChange = (value) => {
    setTouched(true);
    if (value && value.value !== 'Custom') {
      setValue(value.value);
    }

    if (!value) {
      setValue('');
    }
    setSelectedValue(value);
  };
  const className = `my-1 ${useMargin ? 'mb-20' : ''}`;
  return (
    <>
      <Form.Group className={className}>
        <div className="d-flex">
          <Form.Label className="flex-grow-1">
            <b>{label}</b>
            {warningMessage ? <b style={{ color: '#ff9800' }}>{` - ${warningMessage}`}</b> : null}
          </Form.Label>
          {selectedValue && selectedValue.value === 'Custom' && (
            <div style={{ display: 'flex', marginBottom: '-0.05rem' }}>
              <small className="medium-and-up" style={{ alignSelf: 'flex-end' }}>
                {!readOnly && instruction}
              </small>
            </div>
          )}
        </div>
        {readOnly ? (
          <div className="centered">
            {field.value !== undefined ? <span {...props}>{field.value}</span> : <span>&nbsp;&nbsp;</span>}
          </div>
        ) : (
          <>
            <Form.Row>
              <Form.Control
                type="hidden"
                {...field}
                // eslint-disable-next-line no-undef
                {..._.omit(props, ['decimalScale', 'getInputRef', 'id'])}
                isInvalid={meta.error && meta.touched}
              />
              <Col>
                <Select
                  className={props.className}
                  id={`${id}-ddlb`}
                  name={`${id}-ddlb`}
                  {...props}
                  options={options}
                  value={selectedValue}
                  onChange={onSelectChange}
                  onBlur={setTouched}
                  isClearable={props.isClearable}
                  autoFocus={focus}
                />
              </Col>
              <Col>
                <NumericFormat
                  valueIsNumericString={true}
                  id={id}
                  name={name}
                  // disabled={!selectedValue || selectedValue.value !== 'Custom'}
                  onBlur={(e) => {
                    onBlur(e);
                  }}
                  className="form-control input-sm mx-2"
                  thousandSeparator={true}
                  prefix={props.prefix}
                  value={field.value}
                  decimalScale={props.decimalScale || undefined}
                  onValueChange={(value) => {
                    onChange(value.value);
                  }}
                  isAllowed={isAllowed}
                >
                  {props.children}
                </NumericFormat>
              </Col>
            </Form.Row>
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};

export { FormColorInput };

export const FormSelectValueType = ({
  label,
  textValue,
  postChange = false,
  readOnly = false,
  focus = false,
  isAllowed = () => true,
  postBlur,
  warningMessage,
  id,
  name,
  options,
  ...props
}) => {
  const [field, meta, { setValue, setTouched }] = useField(id);
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (field) {
      setSelectedValue(field.value === '' ? '' : options.find((x) => x.value == field.value));
    } else {
      setSelectedValue('');
    }
  }, [field.value]);

  const onSelectChange = (value) => {
    setTouched(true);
    setValue(value?.value ?? '');
    setSelectedValue(value);
  };

  return (
    <>
      <Form.Group className="my-1">
        <div className="d-flex">
          <Form.Label className="flex-grow-1">
            <b>{label}</b>
            {warningMessage ? <b style={{ color: '#ff9800' }}>{` - ${warningMessage}`}</b> : null}
          </Form.Label>
        </div>
        {readOnly ? (
          <div className="centered">
            {field.value !== undefined ? <span {...props}>{field.value}</span> : <span>&nbsp;&nbsp;</span>}
          </div>
        ) : (
          <>
            <Form.Row>
              <Form.Control
                type="hidden"
                {...field}
                // eslint-disable-next-line no-undef
                {..._.omit(props, ['decimalScale', 'getInputRef', 'id', 'isMulti'])}
                isInvalid={meta.error && meta.touched}
              />
              <Col>
                <Select
                  className={props.className}
                  id={`${id}-ddlb`}
                  name={`${id}-ddlb`}
                  {...props}
                  options={options}
                  value={selectedValue}
                  onChange={onSelectChange}
                  onBlur={setTouched}
                  autoFocus={focus}
                />
              </Col>
            </Form.Row>
            <FieldError meta={meta} />
          </>
        )}
      </Form.Group>
    </>
  );
};

export const FormRichTextEditor = ({ label, name, content, mode, onChange, isExpanded, props }) => {
  const editorStateRef = useRef();
  const formikField = name && typeof useField === 'function' ? useField(name) : null;
  const field = formikField ? formikField[0] : null;
  const meta = formikField ? formikField[1] : null;
  const helpers = formikField ? formikField[2] : null;

  const emptyValue =
    '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

  const initialValue =
    field?.value && field.value !== '' ? field.value : content && content !== '' ? content : emptyValue;
  const editorConfig = {
    namespace: 'SAWx Editor',
    nodes: [],
    onError(error) {
      throw error;
    },
    theme: {
      code: 'editor-code',
      heading: {
        h1: 'editor-heading-h1',
        h2: 'editor-heading-h2',
        h3: 'editor-heading-h3',
        h4: 'editor-heading-h4',
        h5: 'editor-heading-h5',
      },
      image: 'editor-image',
      link: 'editor-link',
      list: {
        listitem: 'editor-listitem',
        nested: { listitem: 'editor-nested-listitem' },
        ol: 'editor-list-ol',
        ul: 'editor-list-ul',
      },
      ltr: 'ltr',
      paragraph: 'editor-paragraph',
      placeholder: 'editor-placeholder',
      quote: 'editor-quote',
      rtl: 'rtl',
      text: {
        bold: 'editor-text-bold',
        code: 'editor-text-code',
        hashtag: 'editor-text-hashtag',
        italic: 'editor-text-italic',
        overflowed: 'editor-text-overflowed',
        strikethrough: 'editor-text-strikethrough',
        underline: 'editor-text-underline',
        underlineStrikethrough: 'editor-text-underlineStrikethrough',
      },
    },
    editable: mode === 'EDIT',
    editorState: initialValue,
  };

  const handleChange = (editorState) => {
    const parseState = (state) => {
      const parsed = typeof state === 'string' ? JSON.parse(state) : state;
      if (parsed.text !== undefined) {
        return parsed.text || '';
      }
      return parsed.root.children.map((node) => node.children.map((child) => child.text || '').join('')).join('\n');
    };

    const newText = parseState(editorState);
    const initialText = parseState(initialValue || emptyValue);
    const emptyText = parseState(emptyValue);

    const isUnchanged = newText === initialText;
    const isEmpty = newText === emptyText || newText === '';

    if (isUnchanged || isEmpty) {
      if (helpers && field.value !== initialValue) {
        helpers.setValue(isEmpty ? emptyValue : initialValue, false);
        if (meta.touched) helpers.setTouched(false);
      }
      if (onChange) onChange(editorState, props);
      return;
    }

    if (helpers) helpers.setValue(editorState.editor);
    if (onChange) onChange(editorState, props);
  };

  const useMaxLengthPlugin = () =>
    mode === 'VIEW' ? (
      <MaxLengthPlugin
        maxLength={200}
        mode={mode}
        content={field?.value ?? content}
        editorStateRef={editorStateRef}
        isExpanded={isExpanded}
      />
    ) : (
      <></>
    );

  const historyPlugin = () => (mode === 'EDIT' ? <HistoryPlugin /> : <></>);
  const autoFocusPlugin = () => (mode === 'VIEW' ? <AutoFocusPlugin /> : <></>);

  const editorClass = `editor-container ${mode === 'EDIT' ? 'editor-border' : ''}`;

  return (
    <Form.Group className="my-1">
      <div className="d-flex">
        <Form.Label className="flex-grow-1">
          <b>{label}</b>
        </Form.Label>
      </div>
      <LexicalComposer initialConfig={editorConfig}>
        <div className={editorClass}>
          {mode === 'EDIT' && <ToolbarPlugin />}
          <div className="editor-inner" style={{ backgroundColor: 'transparent' }}>
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<div className="editor-placeholder"></div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            {useMaxLengthPlugin()}
            {historyPlugin()}
            {autoFocusPlugin()}
          </div>
        </div>
        {mode === 'EDIT' && <OnChangePlugin onChange={handleChange} />}
      </LexicalComposer>
    </Form.Group>
  );
};
